import { memo } from 'react';

import moment from 'moment';

import { getByPath } from 'utils/widgets';

import { AvatarComponent } from '@/components/ui/avatar/avatar';
import { WidgetData } from '@/components/ui/widget/widget.type';
import { ActivityView } from '@/components/ui/widget/views/activity/activity.view.type';
import { TextView } from '@/components/ui/widget/views/text/text.view.type';

type Props = {
  view: ActivityView;
  data: WidgetData;
};

const ActivityViewImpl = memo(({ view, data }: Props) => {
  const { who, when } = view;

  const lastActivityDateStr = getByPath(data, when.path);
  const lastActivityAuthor = getByPath(data, who.path);
  const lastActivityAuthorAvatar = getByPath(data, who.avatarPath);

  const lastActivityDate = moment.utc(lastActivityDateStr).local();
  const fromNowDate = lastActivityDate.fromNow();
  const formattedDate = lastActivityDate.format(when.format || 'DD.MM.YY');

  return (
    <div className="flex flex-row flex-nowrap items-center overflow-hidden text-left">
      {who.avatarPath && (
        <div className="mr-2 shrink-0">
          <AvatarComponent
            size={28}
            source={lastActivityAuthorAvatar}
            name={lastActivityAuthor}
          />
        </div>
      )}
      <div className="overflow-hidden">
        <div className="flex flex-row flex-nowrap items-center text-xs font-semibold text-ellipsis">
          {lastActivityAuthor}
        </div>
        <div
          className="text-muted-foreground text-[11px] text-ellipsis"
          title={!when.showCountdown ? fromNowDate : formattedDate}
        >
          {when.showCountdown ? fromNowDate : formattedDate}
        </div>
      </div>
    </div>
  );
});

// sort defaults to text on when (text is ok assuming iso dates)
const getSortConfig = (view: ActivityView): TextView => ({
  type: 'text',
  path: view.when.path
});

// filtering defaults to text search on who
const getFilterConfig = (view: ActivityView): TextView => ({
  type: 'text',
  path: view.who.path
});

// width defaults to 'medium'
const getDefaultWidth = () => 'medium' as const;

// stringifies data at path
const getStringifyFunction = (view: ActivityView) => {
  const { who, when } = view;

  return (data: WidgetData) => {
    const lastActivityDateStr = getByPath(data, when.path);
    const lastActivityDate = moment.utc(lastActivityDateStr).local();
    const lastActivityAuthor = getByPath(data, who.path);
    return `${lastActivityAuthor} - ${lastActivityDate.format(
      when.format || 'DD.MM.YY'
    )}`;
  };
};

export const activityViewProvider = {
  component: ActivityViewImpl,
  getSortConfig,
  getFilterConfig,
  getDefaultWidth,
  getStringifyFunction
};
